import type { Game } from '../../types'
import type { SimGameStatus } from '../../gameweekSimulation'

/** Gameweeks in a season. The season fixture data has exactly this many —
 *  asserted in totalPointsMeta.test.ts so the constant can't drift from it. */
export const SEASON_GAMEWEEKS = 38

export interface TotalPointsMeta {
  /** True only mid-gameweek: some fixtures settled, not all. This — not the
   *  delta's value — is what decides whether the delta renders at all:
   * its presence is the "gameweek in progress" signal, so it
   *  must be absent, not muted, in every other state. */
  isLive: boolean
  /** The meta row's text. Both forms answer the same question at different
   *  scales: how far through the run are we. */
  text: string
}

/**
 * The total-points hero's meta row.
 *
 * Mid-gameweek it counts fixtures ("4 of 10 in"); otherwise it marks the
 * season position ("GW 15 of 38"). The two share one input, which is what
 * makes the handover automatic: the moment the last fixture settles,
 * `settledFixtures === totalFixtures` stops being live, so the row goes
 * straight from "9 of 10 in" to the gameweek form. "10 of 10 in" is not a
 * state that can be reached.
 */
export function computeTotalPointsMeta({
  gameweek,
  settledFixtures,
  totalFixtures,
}: {
  gameweek: number
  settledFixtures: number
  totalFixtures: number
}): TotalPointsMeta {
  const isLive =
    totalFixtures > 0 && settledFixtures > 0 && settledFixtures < totalFixtures

  if (isLive) {
    return { isLive, text: `${settledFixtures} of ${totalFixtures} in` }
  }

  // Clamped so a gameweek past the season's end can never read "GW 39 of 38".
  const displayed = Math.min(Math.max(gameweek, 1), SEASON_GAMEWEEKS)
  return { isLive, text: `GW ${displayed} of ${SEASON_GAMEWEEKS}` }
}

/**
 * How many of the gameweek's fixtures have settled — i.e. exactly the fixtures
 * whose results have already moved the total. Settledness is read the same way
 * GameweekGrid reads it (`isGameRevealed`): the wholesale reveal, or the
 * dev-tools simulation reaching full time on that individual fixture. A
 * revealed fixture with no result yet doesn't count.
 *
 * Derivation only — this neither decides nor triggers settlement.
 */
export function countSettledFixtures({
  games,
  actualResults,
  resultsRevealed,
  simGameStatuses,
}: {
  games: Game[]
  actualResults: Record<string, { homeScore: number; awayScore: number }>
  resultsRevealed: boolean
  simGameStatuses?: Record<string, SimGameStatus>
}): number {
  return games.filter(
    (game) =>
      (resultsRevealed || simGameStatuses?.[game.id] === 'final') &&
      actualResults[game.id]
  ).length
}
